/**
 * Robust WhatsApp share helper.
 *
 * Problems with the old `window.open('https://wa.me/?text=...', '_blank')`:
 *   - Mobile popup blockers (esp. in-app browsers) silently swallow it.
 *   - On iOS Safari, the new-tab gesture chain breaks if the click handler is async/await.
 *   - When called inside a modal with stopPropagation, focus moves and the open() loses its user-activation token.
 *
 * This helper:
 *   1. Prefers `navigator.share` (native sheet — lets user pick WA, Telegram, etc.) when the user-activation is fresh.
 *   2. Falls back to a synthetic anchor-click for wa.me on mobile (no popup blocker).
 *   3. Falls back to clipboard + toast if neither is available.
 *
 * Pass `phone` (digits-only, e.g. "2348012345678") to deep-link a specific contact.
 * Omit `phone` for a "share with anyone" sheet.
 */
export async function shareToWhatsApp({ text, phone = null, title = "Kobolog" }) {
  // 1) Native share sheet (most reliable on mobile)
  if (typeof navigator !== "undefined" && navigator.share) {
    try {
      await navigator.share({ title, text });
      return { ok: true, via: "native" };
    } catch (err) {
      // User cancelled or share API rejected — fall through to wa.me link
      if (err?.name !== "AbortError") {
        console.debug("navigator.share failed, falling back:", err?.message);
      } else {
        return { ok: false, via: "cancelled" };
      }
    }
  }

  // 2) wa.me deep-link via synthetic anchor click (bypasses popup blocker)
  const base = phone
    ? `https://wa.me/${phone.replace(/[^0-9]/g, "")}`
    : "https://wa.me/";
  const url = `${base}?text=${encodeURIComponent(text)}`;
  try {
    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    a.remove();
    return { ok: true, via: "wa.me" };
  } catch (err) {
    console.warn("anchor-click failed", err);
  }

  // 3) Clipboard fallback
  try {
    await navigator.clipboard.writeText(text);
    return { ok: true, via: "clipboard" };
  } catch {
    return { ok: false, via: "none" };
  }
}
